import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  CardActionArea,
  Chip,
  Stack,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Tooltip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useThemeMode } from '../contexts/ThemeContext';
import SEO from '../components/SEO';

const INSIGHTS = [
  {
    id: 'architecture',
    title: 'Platform Architecture',
    description:
      'A visual deep-dive into the Alphanetix multi-layer integration architecture — from connectors and AI agents to compiled workflow execution.',
    category: 'Technical',
    date: 'May 2026',
    tags: ['Architecture', 'iPaaS', 'Technical'],
    htmlFile: '/infographics/ALPHANETIX-ARCHITECTURE-COMPACT.html',
    thumbnail: '/infographics/ALPHANETIX-ARCHITECTURE-COMPACT.png',
    linkedIn: 'https://www.linkedin.com/company/alphanetix/',
  },
  {
    id: 'infographic',
    title: 'Platform at a Glance',
    description:
      'High-density infographic covering connectors, deployment modes, AI orchestration, and the Alphanetix competitive edge — all in one visual.',
    category: 'Overview',
    date: 'May 2026',
    tags: ['Overview', 'AI', 'Automation'],
    htmlFile: '/infographics/ALPHANETIX-INFOGRAPHIC-V5.html',
    thumbnail: '/infographics/ALPHANETIX-INFOGRAPHIC-V5.gif',
    linkedIn: 'https://www.linkedin.com/company/alphanetix/',
  },
  {
    id: 'speed',
    title: 'Speed & Performance',
    description:
      'Compiled workflows vs. interpreted engines. See how Alphanetix achieves sub-millisecond execution times at enterprise scale.',
    category: 'Performance',
    date: 'April 2026',
    tags: ['Performance', 'Technical', 'Benchmarks'],
    htmlFile: '/infographics/ALPHANETIX-SPEED-INFOGRAPHIC.html',
    thumbnail: '/infographics/ALPHANETIX-SPEED-INFOGRAPHIC.gif',
    linkedIn: 'https://www.linkedin.com/company/alphanetix/',
  },
  {
    id: 'enterprise-value',
    title: 'Enterprise Value Proposition',
    description:
      'The quantified business case for enterprise iPaaS — ROI drivers, cost reduction levers, and measurable outcomes across verticals.',
    category: 'Business',
    date: 'March 2026',
    tags: ['Enterprise', 'ROI', 'Value'],
    htmlFile: '/infographics/ALPHANETIX-ENTERPRISE-VALUE.html',
    thumbnail: null,
    linkedIn: 'https://www.linkedin.com/company/alphanetix/',
  },
  {
    id: 'smb-value',
    title: 'SMB Value Proposition',
    description:
      'Why mid-market and SMB teams choose Alphanetix over point solutions — lower TCO, no-code simplicity, and enterprise-grade reliability.',
    category: 'Business',
    date: 'March 2026',
    tags: ['SMB', 'Value', 'No-Code'],
    htmlFile: '/infographics/ALPHANETIX-SMB-VALUE.html',
    thumbnail: null,
    linkedIn: 'https://www.linkedin.com/company/alphanetix/',
  },
  {
    id: 'vs-n8n',
    title: 'Alphanetix vs. n8n: Agentic AI Gap Analysis',
    description:
      'A head-to-head breakdown of Alphanetix agentic AI capabilities against n8n across orchestration depth, security, scalability, and enterprise fit.',
    category: 'Competitive',
    date: 'May 2026',
    tags: ['Competitive', 'AI', 'Analysis'],
    htmlFile: '/infographics/ALPHANETIX-VS-N8N-AGENTIC-AI-GAP-ANALYSIS.html',
    thumbnail: null,
    linkedIn: 'https://www.linkedin.com/company/alphanetix/',
  },
  {
    id: 'business-overview',
    title: 'Business Overview',
    description:
      'Executive summary of the Alphanetix platform — market positioning, target segments, core capabilities, and go-to-market strategy.',
    category: 'Overview',
    date: 'February 2026',
    tags: ['Overview', 'Strategy', 'Market'],
    htmlFile: '/infographics/ALPHANETIX-BUSINESS-OVERVIEW.html',
    thumbnail: null,
    linkedIn: 'https://www.linkedin.com/company/alphanetix/',
  },
];

const CATEGORY_COLORS = {
  Technical: { light: '#6366f1', dark: '#818cf8' },
  Overview: { light: '#0ea5e9', dark: '#38bdf8' },
  'AI & Automation': { light: '#8b5cf6', dark: '#a78bfa' },
  Performance: { light: '#10b981', dark: '#34d399' },
  Business: { light: '#f59e0b', dark: '#fbbf24' },
  Competitive: { light: '#ef4444', dark: '#f87171' },
};

function InsightCard({ insight, onOpen, isLight }) {
  const catColor = CATEGORY_COLORS[insight.category] || { light: '#6366f1', dark: '#818cf8' };
  const accent = isLight ? catColor.light : catColor.dark;

  return (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: isLight
          ? 'rgba(255,255,255,0.7)'
          : 'rgba(255,255,255,0.04)',
        backdropFilter: 'blur(20px)',
        border: isLight
          ? '1px solid rgba(99,102,241,0.12)'
          : '1px solid rgba(255,255,255,0.08)',
        borderRadius: 3,
        overflow: 'hidden',
        transition: 'all 0.3s cubic-bezier(0.175,0.885,0.32,1.275)',
        '&:hover': {
          transform: 'translateY(-6px)',
          boxShadow: isLight
            ? `0 20px 50px rgba(99,102,241,0.15)`
            : `0 20px 50px rgba(0,0,0,0.4)`,
          borderColor: accent + '44',
        },
      }}
    >
      <CardActionArea onClick={() => onOpen(insight)} sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
        {/* Thumbnail / Preview */}
        {insight.thumbnail ? (
          <CardMedia
            component="img"
            image={insight.thumbnail}
            alt={insight.title}
            sx={{
              height: 200,
              objectFit: 'cover',
              borderBottom: isLight
                ? '1px solid rgba(99,102,241,0.08)'
                : '1px solid rgba(255,255,255,0.06)',
            }}
          />
        ) : (
          <Box
            sx={{
              height: 200,
              position: 'relative',
              overflow: 'hidden',
              borderBottom: isLight
                ? '1px solid rgba(99,102,241,0.08)'
                : '1px solid rgba(255,255,255,0.06)',
              // Overlay to block iframe interaction and add tint
              '&::after': {
                content: '""',
                position: 'absolute',
                inset: 0,
                background: isLight
                  ? 'rgba(248,250,252,0.08)'
                  : 'rgba(15,23,42,0.15)',
                zIndex: 1,
                pointerEvents: 'none',
              },
            }}
          >
            <Box
              component="iframe"
              src={insight.htmlFile}
              title={`${insight.title} preview`}
              loading="lazy"
              scrolling="no"
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '1280px',
                height: '800px',
                border: 'none',
                transform: 'scale(0.3)',
                transformOrigin: 'top left',
                pointerEvents: 'none',
                display: 'block',
              }}
            />
          </Box>
        )}

        <CardContent sx={{ flexGrow: 1, p: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
            <Chip
              label={insight.category}
              size="small"
              sx={{
                background: accent + '18',
                color: accent,
                fontWeight: 700,
                fontSize: '0.7rem',
                height: 22,
                border: `1px solid ${accent}33`,
              }}
            />
            <Typography variant="caption" sx={{ color: isLight ? 'rgba(30,41,59,0.45)' : 'rgba(255,255,255,0.35)', fontSize: '0.75rem' }}>
              {insight.date}
            </Typography>
          </Box>

          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              fontSize: '1rem',
              lineHeight: 1.35,
              mb: 1,
              color: isLight ? '#0f172a' : '#f1f5f9',
            }}
          >
            {insight.title}
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: isLight ? 'rgba(30,41,59,0.65)' : 'rgba(255,255,255,0.55)',
              fontSize: '0.82rem',
              lineHeight: 1.6,
              mb: 2,
            }}
          >
            {insight.description}
          </Typography>

          <Stack direction="row" spacing={0.75} flexWrap="wrap" gap={0.75}>
            {insight.tags.map((tag) => (
              <Chip
                key={tag}
                label={tag}
                size="small"
                sx={{
                  height: 20,
                  fontSize: '0.68rem',
                  background: isLight ? 'rgba(30,41,59,0.06)' : 'rgba(255,255,255,0.07)',
                  color: isLight ? 'rgba(30,41,59,0.6)' : 'rgba(255,255,255,0.5)',
                }}
              />
            ))}
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

// ─── Pinch-to-zoom + pan iframe viewer ───────────────────────────────────────
function ZoomableIframe({ src, title }) {
  const containerRef = useRef(null);
  const wrapRef      = useRef(null);
  const overlayRef   = useRef(null);
  const s            = useRef({
    scale: 1, tx: 0, ty: 0, mode: 'idle',
    startDist: 1, startScale: 1,
    startTouchX: 0, startTouchY: 0,
    startTx: 0, startTy: 0,
    startScrollLeft: 0, startScrollTop: 0,
  });

  const IFRAME_W = 1920;
  const IFRAME_H = 1080;
  const MIN_SCALE = 0.3;
  const MAX_SCALE = 4;

  const applyTransform = () => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    wrap.style.transform = `scale(${s.current.scale}) translate(${s.current.tx}px, ${s.current.ty}px)`;
  };

  useEffect(() => {
    const overlay   = overlayRef.current;
    const container = containerRef.current;
    if (!overlay || !container) return;

    const dist = (t1, t2) => {
      const dx = t2.clientX - t1.clientX;
      const dy = t2.clientY - t1.clientY;
      return Math.sqrt(dx * dx + dy * dy);
    };

    const onStart = (e) => {
      const g = s.current;
      if (e.touches.length === 2) {
        e.preventDefault();
        g.mode       = 'pinching';
        g.startDist  = dist(e.touches[0], e.touches[1]);
        g.startScale = g.scale;
        g.startTx    = g.tx;
        g.startTy    = g.ty;
      } else if (e.touches.length === 1) {
        g.startTouchX = e.touches[0].clientX;
        g.startTouchY = e.touches[0].clientY;
        if (g.scale > 1.05) {
          g.mode    = 'panning';
          g.startTx = g.tx;
          g.startTy = g.ty;
        } else {
          g.mode            = 'scrolling';
          g.startScrollLeft = container.scrollLeft;
          g.startScrollTop  = container.scrollTop;
        }
      }
    };

    const onMove = (e) => {
      const g = s.current;
      if (g.mode === 'pinching' && e.touches.length === 2) {
        e.preventDefault();
        const d  = dist(e.touches[0], e.touches[1]);
        g.scale  = Math.max(MIN_SCALE, Math.min(MAX_SCALE, g.startScale * (d / g.startDist)));
        g.tx     = g.startTx;
        g.ty     = g.startTy;
        applyTransform();
      } else if (g.mode === 'panning' && e.touches.length === 1) {
        e.preventDefault();
        g.tx = g.startTx + (e.touches[0].clientX - g.startTouchX) / g.scale;
        g.ty = g.startTy + (e.touches[0].clientY - g.startTouchY) / g.scale;
        applyTransform();
      } else if (g.mode === 'scrolling' && e.touches.length === 1) {
        container.scrollLeft = g.startScrollLeft + (g.startTouchX - e.touches[0].clientX);
        container.scrollTop  = g.startScrollTop  + (g.startTouchY - e.touches[0].clientY);
      }
    };

    const onEnd = () => {
      const g = s.current;
      if (g.mode === 'pinching' && g.scale < MIN_SCALE) {
        g.scale = MIN_SCALE;
        applyTransform();
      }
      g.mode = 'idle';
    };

    // Double-tap to reset zoom
    let lastTap = 0;
    const onTap = (e) => {
      if (e.touches.length !== 0) return;
      const now = Date.now();
      if (now - lastTap < 300) {
        s.current.scale = 1;
        s.current.tx    = 0;
        s.current.ty    = 0;
        applyTransform();
      }
      lastTap = now;
    };

    overlay.addEventListener('touchstart',  onStart, { passive: false });
    overlay.addEventListener('touchmove',   onMove,  { passive: false });
    overlay.addEventListener('touchend',    onEnd);
    overlay.addEventListener('touchcancel', onEnd);
    overlay.addEventListener('touchend',    onTap);

    return () => {
      overlay.removeEventListener('touchstart',  onStart);
      overlay.removeEventListener('touchmove',   onMove);
      overlay.removeEventListener('touchend',    onEnd);
      overlay.removeEventListener('touchcancel', onEnd);
      overlay.removeEventListener('touchend',    onTap);
    };
  }, []);

  return (
    // Scroll container — on desktop iframe fills 100%, on mobile this is the panning base
    <Box
      ref={containerRef}
      sx={{
        width: '100%',
        height: '100%',
        overflow: 'auto',
        WebkitOverflowScrolling: 'touch',
        position: 'relative',
      }}
    >
      {/* Inner wrapper that receives CSS transform for zoom */}
      <Box
        ref={wrapRef}
        sx={{
          width: { xs: IFRAME_W, md: '100%' },
          height: { xs: IFRAME_H, md: '100%' },
          transformOrigin: 'top left',
          willChange: 'transform',
          position: 'relative',
        }}
      >
        <Box
          component="iframe"
          src={src}
          title={title}
          sx={{
            width: { xs: IFRAME_W, md: '100%' },
            height: { xs: IFRAME_H, md: '100%' },
            border: 'none',
            display: 'block',
          }}
        />
        {/* Transparent overlay — captures all touch events on mobile only */}
        <Box
          ref={overlayRef}
          sx={{
            display: { xs: 'block', md: 'none' },
            position: 'absolute',
            inset: 0,
            zIndex: 10,
            touchAction: 'none',
          }}
        />
      </Box>
    </Box>
  );
}

function InsightModal({ insight, open, onClose, isLight }) {
  if (!insight) return null;
  const catColor = CATEGORY_COLORS[insight.category] || { light: '#6366f1', dark: '#818cf8' };
  const accent = isLight ? catColor.light : catColor.dark;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          width: '96vw',
          height: '92vh',
          maxWidth: '1400px',
          m: 'auto',
          borderRadius: { xs: 2, md: 3 },
          background: isLight ? '#f8fafc' : '#0f172a',
          border: isLight ? '1px solid rgba(99,102,241,0.15)' : '1px solid rgba(255,255,255,0.08)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {/* Swipe hint — mobile only */}
      <Box
        sx={{
          display: { xs: 'flex', md: 'none' },
          alignItems: 'center',
          justifyContent: 'center',
          gap: 0.75,
          px: 2,
          py: 0.6,
          background: accent + '18',
          borderBottom: `1px solid ${accent}30`,
          flexShrink: 0,
        }}
      >
        <Typography
          variant="caption"
          sx={{ fontSize: '0.72rem', color: accent, fontWeight: 600, letterSpacing: '0.3px' }}
        >
          ← Pan to scroll · Pinch to zoom · Double-tap to reset →
        </Typography>
      </Box>

      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          py: 1.5,
          px: 2.5,
          borderBottom: isLight ? '1px solid rgba(99,102,241,0.1)' : '1px solid rgba(255,255,255,0.07)',
          background: isLight ? 'rgba(255,255,255,0.8)' : 'rgba(15,23,42,0.9)',
          backdropFilter: 'blur(20px)',
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Chip
            label={insight.category}
            size="small"
            sx={{
              background: accent + '18',
              color: accent,
              fontWeight: 700,
              fontSize: '0.7rem',
              border: `1px solid ${accent}33`,
            }}
          />
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, fontSize: '1rem', color: isLight ? '#0f172a' : '#f1f5f9' }}
          >
            {insight.title}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Tooltip title="View on LinkedIn">
            <IconButton
              component="a"
              href={insight.linkedIn}
              target="_blank"
              rel="noopener noreferrer"
              size="small"
              sx={{
                color: isLight ? 'rgba(30,41,59,0.5)' : 'rgba(255,255,255,0.4)',
                '&:hover': { color: '#0077b5' },
              }}
            >
              <LinkedInIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Open in new tab">
            <IconButton
              component="a"
              href={insight.htmlFile}
              target="_blank"
              rel="noopener noreferrer"
              size="small"
              sx={{
                color: isLight ? 'rgba(30,41,59,0.5)' : 'rgba(255,255,255,0.4)',
                '&:hover': { color: accent },
              }}
            >
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              color: isLight ? 'rgba(30,41,59,0.5)' : 'rgba(255,255,255,0.4)',
              '&:hover': { color: isLight ? '#0f172a' : '#f1f5f9' },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{
          p: 0,
          flexGrow: 1,
          overflow: 'hidden',
          '&.MuiDialogContent-root': { p: 0 },
        }}
      >
        <ZoomableIframe src={insight.htmlFile} title={insight.title} />
      </DialogContent>
    </Dialog>
  );
}

export default function Insights() {
  const { isLight } = useThemeMode();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [selectedInsight, setSelectedInsight] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const handleOpen = (insight) => {
    setSelectedInsight(insight);
    setModalOpen(true);
  };

  const handleClose = () => {
    setModalOpen(false);
  };

  return (
    <>
      <SEO
        title="Insights — Alphanetix"
        description="Visual insights, infographics, and technical deep-dives from the Alphanetix team. Explore platform architecture, AI pipelines, competitive analysis, and more."
        keywords="enterprise iPaaS insights, AI automation infographics, integration platform analysis, Alphanetix blog"
        ogImage="/images/company_logo_dark.jpg"
      />

      <Box
        sx={{
          minHeight: '100vh',
          pt: { xs: 10, md: 14 },
          pb: 10,
        }}
      >
        <Container maxWidth="lg">
          {/* Page header */}
          <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
            <Chip
              label="Knowledge Hub"
              size="small"
              icon={<AutoAwesomeIcon sx={{ fontSize: '0.85rem !important' }} />}
              sx={{
                mb: 2,
                background: isLight ? 'rgba(99,102,241,0.1)' : 'rgba(129,140,248,0.15)',
                color: isLight ? '#6366f1' : '#818cf8',
                fontWeight: 700,
                fontSize: '0.75rem',
                border: isLight ? '1px solid rgba(99,102,241,0.2)' : '1px solid rgba(129,140,248,0.2)',
              }}
            />
            <Typography
              variant="h2"
              component="h1"
              sx={{
                fontWeight: 800,
                fontSize: { xs: '2rem', md: '3rem' },
                lineHeight: 1.15,
                mb: 2,
                background: isLight
                  ? 'linear-gradient(135deg, #0f172a 0%, #4f46e5 100%)'
                  : 'linear-gradient(135deg, #f1f5f9 0%, #818cf8 60%, #38bdf8 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              Insights
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: isLight ? 'rgba(30,41,59,0.65)' : 'rgba(255,255,255,0.55)',
                maxWidth: 560,
                mx: 'auto',
                fontSize: { xs: '0.95rem', md: '1.05rem' },
                lineHeight: 1.7,
              }}
            >
              Visual deep-dives into enterprise integration, agentic AI, and the Alphanetix platform — from architecture diagrams to competitive analysis.
            </Typography>

            {/* LinkedIn follow CTA */}
            <Box
              component="a"
              href="https://www.linkedin.com/company/alphanetix/"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                mt: 3,
                px: 2.5,
                py: 1,
                borderRadius: 2,
                border: isLight ? '1px solid rgba(0,119,181,0.3)' : '1px solid rgba(0,119,181,0.4)',
                background: isLight ? 'rgba(0,119,181,0.06)' : 'rgba(0,119,181,0.1)',
                color: '#0077b5',
                textDecoration: 'none',
                fontWeight: 600,
                fontSize: '0.85rem',
                transition: 'all 0.25s ease',
                '&:hover': {
                  background: 'rgba(0,119,181,0.15)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 20px rgba(0,119,181,0.2)',
                },
              }}
            >
              <LinkedInIcon sx={{ fontSize: '1.1rem' }} />
              Follow us on LinkedIn
            </Box>
          </Box>

          {/* Insights grid */}
          <Grid container spacing={3}>
            {INSIGHTS.map((insight) => (
              <Grid item xs={12} sm={6} md={4} key={insight.id}>
                <InsightCard insight={insight} onOpen={handleOpen} isLight={isLight} />
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <InsightModal
        insight={selectedInsight}
        open={modalOpen}
        onClose={handleClose}
        isLight={isLight}
      />
    </>
  );
}
